// `SourceFetcher<TsRefSource>` — resolves CDF timeseries references
// into `MaterializedDataset`s matching `TS_REF_INNER_SCHEMA`.
//
// Cell-level scaling. The engine invokes the fetcher once per unique
// `TsRefSource`; with one source per cell, a 10-col × 1000-row qkg
// result that calls `(unnest production)` would produce 10000
// independent fetcher invocations. To collapse those into a single
// CDF round-trip (where possible), `buildTsRefFetcher` runs a
// DataLoader-style microtask batcher in its closure:
//
//   - Each invocation pushes a `{externalId, resolve, reject}` onto
//     a per-fetcher pending queue and schedules a flush via
//     `queueMicrotask` (idempotent).
//   - The flush splices the current queue, dedupes externalIds, and
//     hands them to `fetchDatapointsBatched` — which chunks at
//     `CDF_DATAPOINTS_BATCH_SIZE` (100) and parallelises chunks via
//     TanStack's `fetchQuery` (so identical requests across flushes
//     share the cache entry).
//   - Per-series result rows are dispatched back to their queued
//     promises; series missing from the response (unknown id /
//     empty window) resolve to empty MaterializedDatasets matching
//     the inner schema.
//
// The `start`/`end` window is pinned at fetcher-construct time so
// every flush within the fetcher's lifetime produces a stable
// TanStack cache key. The session-level memo (`session.fetch` keyed
// per source descriptor) handles cross-flush dedup of identical
// `externalId` requests — first burst materialises, subsequent calls
// hit the cache.
//
// `:limit` per series scales with the batch's distinct-id count
// (mirrors `fetchTimeseriesStep`'s budget split): wider batches get
// a smaller per-series cap so the total payload stays under
// `MAX_RAW_POINTS_PER_BATCH`.

import type { Aggregate, CogniteClient } from '@cognite/sdk';
import type { QueryClient } from '@tanstack/react-query';

import { TS_REF_INNER_SCHEMA } from '#core/lib/cognite/view.ts';
import type { SourceFetcher } from '#core/lib/expr/EvalContext.ts';
import type { TsRefBucketHint, TsRefSource } from '#core/lib/expr/plan.ts';
import { type Cell, type Columns, MaterializedDataset } from '#core/lib/expr/value.ts';
import { CDF_DATAPOINTS_BATCH_SIZE } from '#core/lib/timeseries/cdf-constants.ts';
import {
  type DatapointsBatchResult,
  type DatapointsFetchSpec,
  fetchDatapointsBatched,
} from '#core/lib/timeseries/fetch-datapoints-batched.ts';

/** Default rolling window for raw-datapoints lookups (last 90 days). */
export const TS_REF_DEFAULT_WINDOW_DAYS = 90;

/**
 * Hard cap on total raw-datapoints returned per batched CDF call.
 * Scales the per-series `limit` down as the batch widens — mirrors
 * `fetchTimeseriesStep`'s `maxDatapoints / seriesPerBatch` budget.
 */
export const MAX_RAW_POINTS_PER_BATCH = 100_000;

/**
 * Compute the per-series datapoint limit for a batch of `seriesCount`
 * distinct timeseries. Caps the batch at `CDF_DATAPOINTS_BATCH_SIZE`
 * (the SDK's per-call limit); divides `MAX_RAW_POINTS_PER_BATCH` evenly
 * across the smaller of the two.
 */
export const computePerSeriesLimit = (seriesCount: number): number => {
  const effective = Math.min(Math.max(seriesCount, 1), CDF_DATAPOINTS_BATCH_SIZE);
  return Math.floor(MAX_RAW_POINTS_PER_BATCH / effective);
};

/**
 * Project a single CDF series response into the column-major `Columns`
 * shape declared by `TS_REF_INNER_SCHEMA`. `isString` and `unit` live
 * at the series level on the CDF response (per `DatapointsMetadata`);
 * the inner schema broadcasts them onto every row so downstream verbs
 * can filter / aggregate without re-joining series metadata.
 *
 * Accepts `DatapointsBatchResult` (= `DatapointAggregates | Datapoints`)
 * so both raw and aggregated responses route through the same
 * projection — the SDK union shares `datapoints[].timestamp`. When
 * `aggField` is set (server-side downsampling was requested), the
 * inner `value` column is read from that aggregate field
 * (`average` / `min` / `max` / `sum` / `count`) instead of `.value`;
 * raw responses (no `aggField`) read `.value`.
 *
 * Absent (`undefined`), `null`, and empty-string values all normalise
 * to `null`. The inner `TS_REF_INNER_SCHEMA` declares `value` as
 * `numberCol`, so an empty string is already a schema violation; left
 * intact it both degrades the downstream column type to `unknown` and
 * poisons positional `first`/`last` aggregates over the boundary
 * datapoint.
 */
export const seriesToColumns = (series: DatapointsBatchResult, aggField?: Aggregate): Columns => {
  const points = series.datapoints as unknown as readonly Record<string, unknown>[];
  const isString = series.isString === true;
  const unit = series.unit ?? null;
  const readValue = (p: Record<string, unknown>): unknown => (aggField === undefined ? p.value : p[aggField]);
  return {
    timestamp: points.map((p) => p.timestamp as Cell),
    value: points.map((p) => {
      const v = readValue(p);
      return v === undefined || v === null || v === '' ? null : (v as Cell);
    }),
    isString: points.map(() => isString as Cell),
    unit: points.map(() => unit as Cell),
  };
};

/** Empty `Columns` matching the inner schema's column set. */
const emptyColumns = (): Columns =>
  Object.fromEntries(Array.from(TS_REF_INNER_SCHEMA.keys()).map((k) => [k, [] as Cell[]]));

type PendingRequest = {
  readonly externalId: string;
  /** Stable key for the derived fetch spec — flush groups by this. */
  readonly specKey: string;
  /** CDF fetch spec (window ± server-side downsampling) for this cell. */
  readonly spec: DatapointsFetchSpec;
  readonly resolve: (series: DatapointsBatchResult | undefined) => void;
  readonly reject: (error: unknown) => void;
};

// Bare `time-bucket :agg` symbol → CDF aggregate. Only these collapse
// exactly to one per-bucket value; `first`/`last`/`value`/… have no
// CDF equivalent → raw fallback.
const AGG_TO_CDF: Readonly<Record<string, Aggregate>> = {
  mean: 'average',
  min: 'min',
  max: 'max',
  sum: 'sum',
  count: 'count',
};

// `time-bucket :period` → CDF granularity. month/quarter/year have no
// native CDF granularity → raw fallback (the window is still honoured).
const PERIOD_TO_GRANULARITY: Readonly<Record<string, string>> = {
  minute: '1m',
  hour: '1h',
  day: '1d',
  week: '7d',
};

const parseBound = (iso: string | undefined): number | undefined => {
  if (iso === undefined) {
    return undefined;
  }
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? undefined : ms;
};

/**
 * Translate the engine's `TsRefBucketHint` into a concrete CDF fetch
 * spec. `:window` always sets `[start, end]` — the fix for the fixed
 * 90-day window that made long `:window`s return nothing. Server-side
 * downsampling is requested ONLY when it is exact: a single `:agg`
 * symbol mapping to a CDF aggregate AND a `:period` mapping to a CDF
 * granularity — so the client-side `time-bucket` re-bucket at the same
 * period is an identity. Every other shape (multi-agg, first/last,
 * month+ periods, no hint) falls back to a raw fetch within the
 * window. `aggField` tells `seriesToColumns` which aggregate field
 * carries the value.
 */
const bucketToFetch = (
  bucket: TsRefBucketHint | undefined,
  defaultStart: number,
  defaultEnd: number,
): { readonly spec: DatapointsFetchSpec; readonly specKey: string; readonly aggField?: Aggregate } => {
  const start = parseBound(bucket?.window?.[0]) ?? defaultStart;
  const end = parseBound(bucket?.window?.[1]) ?? defaultEnd;
  const aggSym = bucket?.aggs?.length === 1 ? bucket.aggs[0] : undefined;
  const cdfAgg = aggSym === undefined ? undefined : AGG_TO_CDF[aggSym];
  const granularity = bucket?.period === undefined ? undefined : PERIOD_TO_GRANULARITY[bucket.period];
  if (cdfAgg !== undefined && granularity !== undefined) {
    const spec: DatapointsFetchSpec = { start, end, granularity, aggregates: [cdfAgg] };
    return { spec, specKey: JSON.stringify(spec), aggField: cdfAgg };
  }
  const spec: DatapointsFetchSpec = { start, end };
  return { spec, specKey: JSON.stringify(spec) };
};

/**
 * Inline `SourceFetcher<TsRefSource>` factory with closure-scoped
 * microtask batching. See the file header for the request-coalescing
 * contract. The fetch spec is derived per source from its
 * `TsRefBucketHint` (window ± server-side downsampling); the flush
 * groups pending requests by spec so cells whose downstream
 * `time-bucket` asked for different windows/granularities don't get
 * forced into one incompatible CDF call.
 */
export const buildTsRefFetcher = (client: CogniteClient, queryClient: QueryClient): SourceFetcher<TsRefSource> => {
  // Default rolling raw window, pinned at construct time so a hint-less
  // source still produces a stable TanStack cache key across flushes.
  const defaultEnd = Date.now();
  const defaultStart = defaultEnd - TS_REF_DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000;

  const pending: PendingRequest[] = [];
  let flushScheduled = false;

  const flush = async (): Promise<void> => {
    flushScheduled = false;
    const batch = pending.splice(0);
    if (batch.length === 0) {
      return;
    }
    // One CDF call per distinct fetch spec (window + granularity +
    // aggregates). Within a group, externalIds are deduped + chunked
    // by `fetchDatapointsBatched`.
    const groups = new Map<string, { spec: DatapointsFetchSpec; reqs: PendingRequest[] }>();
    for (const req of batch) {
      const g = groups.get(req.specKey);
      if (g === undefined) {
        groups.set(req.specKey, { spec: req.spec, reqs: [req] });
      } else {
        g.reqs.push(req);
      }
    }
    await Promise.all(
      Array.from(groups.values()).map(async ({ spec, reqs }) => {
        const externalIds = Array.from(new Set(reqs.map((r) => r.externalId)));
        const limit = computePerSeriesLimit(externalIds.length);
        try {
          const result = await fetchDatapointsBatched({
            client,
            queryClient,
            items: externalIds.map((externalId) => ({ externalId })),
            spec: { ...spec, limit },
          });
          for (const req of reqs) {
            req.resolve(result.get(req.externalId));
          }
        } catch (e) {
          for (const req of reqs) {
            req.reject(e);
          }
        }
      }),
    );
  };

  const scheduleFlush = (): void => {
    if (flushScheduled) {
      return;
    }
    flushScheduled = true;
    queueMicrotask(flush);
  };

  return async (source) => {
    const { spec, specKey, aggField } = bucketToFetch(source.bucket, defaultStart, defaultEnd);
    const series = await new Promise<DatapointsBatchResult | undefined>((resolve, reject) => {
      pending.push({ externalId: source.externalId, specKey, spec, resolve, reject });
      scheduleFlush();
    });
    if (series === undefined) {
      // Missing from response: unknown externalId or no data in
      // window. Resolve with an empty MaterializedDataset matching the
      // inner schema so cell consumers degrade gracefully (a downstream
      // `(unnest …)` produces zero rows for this cell).
      return new MaterializedDataset(emptyColumns(), TS_REF_INNER_SCHEMA);
    }
    return new MaterializedDataset(seriesToColumns(series, aggField), TS_REF_INNER_SCHEMA);
  };
};
